import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Stack } from "@mui/material";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import {
  ActivityEntityType,
  ActivityEventType,
  type ActivityLogListItemDto,
  ActivityLogSortColumn,
  SortOrder,
} from "Dashboard/Api/activityLogApi";
import { useActivityLogQuery } from "Dashboard/Api/Queries/useActivityLogQuery";
import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";

const PAGE_SIZE = 25;

const SORTABLE_COLUMNS = [
  "dateCreated",
  "userFullName",
  "activityEventType",
  "note",
] as const;

const SORT_COLUMN_MAP: Record<string, ActivityLogSortColumn> = {
  dateCreated: ActivityLogSortColumn.DateCreated,
  userFullName: ActivityLogSortColumn.UserFullName,
  activityEventType: ActivityLogSortColumn.ActivityEventType,
  note: ActivityLogSortColumn.Note,
};

function getEventTypeLabel(
  eventType: ActivityEventType,
  t: (key: string) => string,
): string {
  const map: Record<ActivityEventType, string> = {
    [ActivityEventType.ActorCreated]: t(Resources.ActivityLog.ActorCreated),
    [ActivityEventType.ActorPriceCategoryChanged]: t(
      Resources.ActivityLog.ActorPriceCategoryChanged,
    ),
    [ActivityEventType.PartnerCreated]: t(Resources.ActivityLog.PartnerCreated),
    [ActivityEventType.ProjectCreated]: t(Resources.ActivityLog.ProjectCreated),
    [ActivityEventType.ProjectCompleted]: t(
      Resources.ActivityLog.ProjectCompleted,
    ),
    [ActivityEventType.ProjectClosed]: t(Resources.ActivityLog.ProjectClosed),
    [ActivityEventType.JobCompleted]: t(Resources.ActivityLog.JobCompleted),
    [ActivityEventType.PartnerCreditTopUp]: t(
      Resources.ActivityLog.PartnerCreditTopUp,
    ),
    [ActivityEventType.ActorPayoutApproved]: t(
      Resources.ActivityLog.ActorPayoutApproved,
    ),
    [ActivityEventType.ClientCreated]: t(Resources.ActivityLog.ClientCreated),
  };
  return map[eventType] ?? eventType;
}

function getEntityDetailPath(
  entityType: ActivityEntityType,
  entityID?: string | null,
  parentEntityID?: string | null,
): string | null {
  if (!entityID) return null;

  switch (entityType) {
    case ActivityEntityType.Actor:
      return AppRouting.ActorDetail.path(entityID);
    case ActivityEntityType.Partner:
      return AppRouting.PartnerDetail.path(entityID);
    case ActivityEntityType.Client:
      return AppRouting.ClientDetail.path(entityID);
    case ActivityEntityType.Project:
      return AppRouting.ProjectDetail.path(entityID);
    case ActivityEntityType.Job:
      return parentEntityID
        ? AppRouting.ProjectJobDetail.path(parentEntityID, entityID)
        : null;
    default:
      return null;
  }
}

export const ActivityLogTable = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();

  const [selectedEventType, setSelectedEventType] = useQueryState(
    "eventType",
    parseAsStringEnum<ActivityEventType>(Object.values(ActivityEventType)),
  );

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    pageSize: PAGE_SIZE,
    validSortIds: [...SORTABLE_COLUMNS],
    defaultSort: { id: "dateCreated", desc: true },
  });

  const sortBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useActivityLogQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    activityEventType: selectedEventType ?? undefined,
    sortBy,
    sortOrder,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const eventTypeOptions = useMemo(
    () => [
      { value: "", label: t(Resources.ActivityLog.AllEventTypes) },
      ...Object.values(ActivityEventType).map((type) => ({
        value: type,
        label: getEventTypeLabel(type, t),
      })),
    ],
    [t],
  );

  const columns: ColumnDef<ActivityLogListItemDto>[] = useMemo(
    () => [
      {
        id: "dateCreated",
        header: t(Resources.ActivityLog.Timestamp),
        accessorFn: (row) =>
          new Date(row.dateCreated).toLocaleString(undefined, {
            dateStyle: "short",
            timeStyle: "short",
          }),
        meta: { maxWidth: 160 },
      },
      {
        id: "userFullName",
        header: t(Resources.ActivityLog.User),
        accessorKey: "userFullName",
        meta: { maxWidth: 200 },
      },
      {
        id: "activityEventType",
        header: t(Resources.ActivityLog.Event),
        accessorFn: (row) => getEventTypeLabel(row.activityEventType, t),
        meta: { maxWidth: 220 },
      },
      {
        id: "note",
        header: t(Resources.ActivityLog.Note),
        accessorKey: "note",
        meta: { fillRow: true },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const path = getEntityDetailPath(
            row.original.activityEntityType,
            row.original.entityID,
            row.original.parentEntityID,
          );
          if (!path) return null;
          return (
            <IconButton
              size="small"
              onClick={() => navigate(path)}
              aria-label={t(Resources.ActivityLog.Detail)}
              sx={{ mr: 1 }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          );
        },
        meta: { maxWidth: 56 },
      },
    ],
    [t, navigate],
  );

  const table = useReactTable({
    data: items,
    columns,
    getRowId: (row) => row.activityLogID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ minWidth: 220 }}>
          <BlDropdown
            name="event-type-filter"
            options={eventTypeOptions}
            value={selectedEventType ?? ""}
            onChange={(value) => {
              setSelectedEventType((value as ActivityEventType) || null);
              resetPage();
            }}
            placeholder={t(Resources.ActivityLog.AllEventTypes)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
